import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Pin, Lock, Quote, Trash2, Pencil, MessageSquarePlus,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import Breadcrumbs from "@/components/Breadcrumbs";
import WysiwygEditor from "@/components/WysiwygEditor";
import Reactions from "@/components/Reactions";
import ReportDialog from "@/components/ReportDialog";
import PrefixBadge from "@/components/PrefixBadge";
import BadgesRow from "@/components/BadgesRow";
import SubscribeButton from "@/components/SubscribeButton";
import { Pagination } from "@/pages/ForumPage";
import { useAuth } from "@/contexts/AuthContext";
import { t } from "@/lib/i18n";
import { timeAgo, fmtDate, initials, roleBadge } from "@/lib/format";
import { toast } from "sonner";

export default function ThreadPage() {
  const { threadId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [reply, setReply] = useState("");
  const [quoted, setQuoted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(null); // {id, content}
  const { user } = useAuth();
  const nav = useNavigate();
  const replyRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/threads/${threadId}?page=${page}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [threadId, page]);

  useEffect(() => { load(); }, [load]);

  const canMod = user && (user.role === "admin" || user.role === "moderator");

  const scrollToReply = () => {
    setTimeout(() => replyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const doQuote = (post) => {
    setQuoted({ id: post.id, username: post.author.username, content: post.content });
    scrollToReply();
  };

  const submitReply = async () => {
    if (!user) { toast.error("لاگ اِن کریں"); nav("/login"); return; }
    if (!reply || reply === "<br>" || reply.replace(/<[^>]+>/g, "").trim() === "") {
      toast.error("پیغام خالی نہیں ہو سکتا");
      return;
    }
    setSubmitting(true);
    try {
      let payload = { thread_id: threadId, content: reply, quoted_post_id: quoted?.id || null };
      // prepend quote block visually
      if (quoted) {
        payload.content = `<blockquote><div class="quote-head">${quoted.username}:</div>${quoted.content}</blockquote>${reply}`;
      }
      await api.post("/posts", payload);
      setReply("");
      setQuoted(null);
      toast.success("جواب پوسٹ ہو گیا");
      // Force editor remount
      setPage((p) => p);
      load();
    } catch (e) {
      toast.error("جواب پوسٹ نہیں ہو سکا");
    } finally {
      setSubmitting(false);
    }
  };

  const togglePin = async () => {
    const r = await api.post(`/threads/${threadId}/pin`);
    toast.success(r.data.is_pinned ? "دھاگا اہم کر دیا" : "اہمیت ہٹا دی");
    load();
  };
  const toggleLock = async () => {
    const r = await api.post(`/threads/${threadId}/lock`);
    toast.success(r.data.is_locked ? "دھاگا مقفل" : "دھاگا کھول دیا");
    load();
  };
  const deleteThread = async () => {
    if (!window.confirm(t.reallyDelete)) return;
    await api.delete(`/threads/${threadId}`);
    toast.success("حذف ہو گیا");
    nav(`/f/${data.forum.id}`);
  };

  const deletePost = async (post) => {
    if (!window.confirm(t.reallyDelete)) return;
    await api.delete(`/posts/${post.id}`);
    toast.success("حذف ہو گیا");
    load();
  };

  const startEdit = (post) => setEditing({ id: post.id, content: post.content });
  const saveEdit = async () => {
    await api.put(`/posts/${editing.id}`, { content: editing.content });
    toast.success("محفوظ");
    setEditing(null);
    load();
  };

  return (
    <div className="space-y-6" data-testid="thread-page">
      {data && (
        <>
          <Breadcrumbs
            items={[
              { label: t.home, to: "/" },
              { label: data.category?.name, to: "/" },
              { label: data.forum?.name, to: `/f/${data.forum.id}` },
              { label: data.thread.title },
            ]}
          />

          <div className="flex flex-wrap items-start justify-between gap-4 border-b-2 border-primary pb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {data.thread.prefix && <PrefixBadge prefix={data.thread.prefix} />}
                {data.thread.is_pinned && (
                  <Badge className="bg-accent text-accent-foreground font-eng text-[10px]">
                    <Pin size={11} className="ms-1" /> {t.pinned}
                  </Badge>
                )}
                {data.thread.is_locked && (
                  <Badge variant="secondary" className="font-eng text-[10px]">
                    <Lock size={11} className="ms-1" /> {t.locked}
                  </Badge>
                )}
              </div>
              <h1 className="urdu-naskh text-3xl font-semibold text-primary leading-tight">{data.thread.title}</h1>
            </div>

            <div className="flex items-center gap-2 font-eng text-xs flex-wrap">
              {user && <SubscribeButton threadId={data.thread.id} initialSubscription={data.subscription} />}
              {canMod && (
                <>
                  <Button size="sm" variant="outline" onClick={togglePin} data-testid="mod-pin">
                    <Pin size={14} className="ms-1" /> {data.thread.is_pinned ? t.unpin : t.pin}
                  </Button>
                  <Button size="sm" variant="outline" onClick={toggleLock} data-testid="mod-lock">
                    <Lock size={14} className="ms-1" /> {data.thread.is_locked ? t.unlock : t.lock}
                  </Button>
                  <Button size="sm" variant="destructive" onClick={deleteThread} data-testid="mod-delete-thread">
                    <Trash2 size={14} className="ms-1" /> {t.delete}
                  </Button>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {loading && (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {data?.posts?.map((p, idx) => (
        <Card
          key={p.id}
          id={`post-${p.id}`}
          className={`overflow-hidden ${p.is_op ? "border-primary/40 border-2" : ""}`}
          data-testid={`post-${p.id}`}
        >
          <div className="flex flex-col md:flex-row-reverse">
            {/* Author sidebar (right in RTL = flex-row-reverse) */}
            <aside className="md:w-48 shrink-0 bg-muted/60 border-b md:border-b-0 md:border-s border-border p-4 flex md:flex-col items-center md:items-start gap-3">
              <Link to={`/u/${p.author.username}`} className="flex flex-col items-center md:items-start gap-2">
                <Avatar className="h-16 w-16 border-2 border-primary/30">
                  <AvatarImage src={p.author.avatar_url || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary font-eng font-semibold">
                    {initials(p.author.username)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-eng font-semibold text-primary hover:underline">{p.author.username}</div>
                  {roleBadge(p.author.role) && (
                    <span className={`inline-block mt-1 text-[10px] font-eng font-semibold px-1.5 py-0.5 rounded ${roleBadge(p.author.role).cls}`}>
                      {roleBadge(p.author.role).label}
                    </span>
                  )}
                </div>
              </Link>
              <div className="text-[11px] font-eng text-muted-foreground md:mt-2 md:space-y-0.5">
                <div>{t.postCount}: <b className="text-foreground">{p.author.post_count || 0}</b></div>
                <div>{t.joined}: <b className="text-foreground">{p.author.created_at ? fmtDate(p.author.created_at).split(",")[0] : "—"}</b></div>
              </div>
            </aside>

            {/* Post body */}
            <div className="flex-1 min-w-0 flex flex-col">
              <div className="flex items-center justify-between px-4 sm:px-5 py-2 border-b border-border bg-card">
                <div className="text-xs text-muted-foreground font-eng flex items-center gap-2">
                  <span>#{(page - 1) * data.pagination.limit + idx + 1}</span>
                  <span>·</span>
                  <span>{fmtDate(p.created_at)}</span>
                  {p.edited_at && <span className="italic">· edited {timeAgo(p.edited_at)}</span>}
                </div>
                <div className="flex items-center gap-1">
                  {user && !data.thread.is_locked && (
                    <Button size="sm" variant="ghost" onClick={() => doQuote(p)} title={t.quote} data-testid={`quote-${p.id}`}>
                      <Quote size={14} />
                    </Button>
                  )}
                  {user && user.id !== p.user_id && <ReportDialog postId={p.id} />}
                  {user && (user.id === p.user_id || canMod) && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => startEdit(p)} title={t.edit} data-testid={`edit-${p.id}`}>
                        <Pencil size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deletePost(p)} title={t.delete} className="text-destructive" data-testid={`delete-${p.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <div className="p-5 flex-1">
                {editing?.id === p.id ? (
                  <div className="space-y-2">
                    <WysiwygEditor
                      value={editing.content}
                      onChange={(v) => setEditing((e) => ({ ...e, content: v }))}
                      minHeight={140}
                      testid={`edit-editor-${p.id}`}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => setEditing(null)}>{t.cancel}</Button>
                      <Button size="sm" onClick={saveEdit} className="bg-primary" data-testid={`save-edit-${p.id}`}>{t.save}</Button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="post-content"
                    dangerouslySetInnerHTML={{ __html: p.content }}
                  />
                )}
                {p.signature && !editing && (
                  <div className="mt-4 pt-3 border-t border-dashed border-border text-xs opacity-75 urdu-naskh" dangerouslySetInnerHTML={{ __html: p.signature }} />
                )}
              </div>

              <div className="px-5 py-2 border-t border-border bg-muted/40 flex items-center justify-between gap-3 flex-wrap">
                <Reactions
                  postId={p.id}
                  initialSummary={p.reactions_summary}
                  initialMine={p.my_reaction}
                />
                {p.author.badges?.length > 0 && (
                  <BadgesRow badges={p.author.badges.slice(0, 3)} />
                )}
              </div>
            </div>
          </div>
        </Card>
      ))}

      {data && data.pagination.pages > 1 && (
        <Pagination page={page} pages={data.pagination.pages} onChange={setPage} />
      )}

      {/* Reply box */}
      {data && (
        <div ref={replyRef} className="pt-4">
          {data.thread.is_locked ? (
            <Card className="p-6 text-center urdu-naskh text-muted-foreground">
              <Lock className="inline-block ms-1" size={16} /> یہ دھاگا مقفل ہے، جواب نہیں دیا جا سکتا۔
            </Card>
          ) : user ? (
            <Card className="p-4 space-y-3" data-testid="reply-card">
              <div className="flex items-center gap-2">
                <MessageSquarePlus size={16} className="text-primary" />
                <h3 className="urdu-naskh text-lg font-semibold text-primary">{t.postReply}</h3>
              </div>
              <WysiwygEditor
                key={data.posts.length}
                value={reply}
                onChange={setReply}
                quoted={quoted}
                onRemoveQuote={() => setQuoted(null)}
                placeholder={t.yourReply}
                minHeight={140}
              />
              <div className="flex justify-end">
                <Button onClick={submitReply} disabled={submitting} className="bg-primary" data-testid="submit-reply">
                  {submitting ? t.loading : t.postReply}
                </Button>
              </div>
            </Card>
          ) : (
            <Card className="p-6 text-center">
              <p className="urdu-naskh mb-3">{t.loginRequired}</p>
              <div className="flex gap-2 justify-center">
                <Button asChild><Link to="/login">{t.login}</Link></Button>
                <Button asChild variant="outline"><Link to="/register">{t.register}</Link></Button>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
